'use client';

import { useEffect, useRef, useState } from 'react';
import { trackEmerald } from '../analytics';
import { useFlowDispatch } from '../FlowProvider';
import { hapMicro } from '../haptics';
import { isCoarsePointerDevice } from '../pointerType';
import { useSphereHandle } from '../sphereContext';
import {
  FauxCaret,
  Field,
  FieldFrame,
  Ghost,
  Heading,
  NameQuestion,
  PrimaryCta,
  Scene,
  SceneBottom,
  SceneSpacer,
  SceneTop,
} from '../styles';

export type NameSceneProps = {
  active: boolean;
};

export const NameScene = ({ active }: NameSceneProps) => {
  const dispatch = useFlowDispatch();
  const sphereRef = useSphereHandle();
  const inputRef = useRef<HTMLInputElement>(null);
  // Gates the decorative FauxCaret: true only after a genuine focus event.
  const [focused, setFocused] = useState(false);
  const [hasText, setHasText] = useState(false);

  useEffect(() => {
    if (!active) return;
    setHasText(false);
    setFocused(false);
    // Non-touch only — auto-focusing a touchscreen field breaks the iOS soft
    // keyboard. See pointerType.ts.
    if (!isCoarsePointerDevice()) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [active]);

  const submit = () => {
    const value = inputRef.current?.value.trim() ?? '';
    if (!value) return;
    // Event only — never the name itself (privacy; see analytics.ts).
    trackEmerald('emerald_name_submitted');
    sphereRef.current?.pulse(1.1);
    dispatch({ type: 'SUBMIT_NAME', name: value });
  };

  return (
    <Scene $on={active} aria-hidden={!active}>
      <SceneTop>
        <NameQuestion>
          <Heading>
            What should the UltraSphere
            <br />
            call you?
          </Heading>
        </NameQuestion>
        <FieldFrame>
          <Field
            ref={inputRef}
            type="text"
            maxLength={20}
            autoComplete="off"
            autoCapitalize="words"
            spellCheck={false}
            enterKeyHint="done"
            aria-label="Your name"
            onFocus={() => setFocused(true)}
            onInput={(e) => {
              setHasText(e.currentTarget.value.trim().length > 0);
              sphereRef.current?.triggerRipple([
                (Math.random() - 0.5) * 1.2,
                (Math.random() - 0.5) * 1.2,
              ]);
              sphereRef.current?.pulse(0.25);
              hapMicro();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
          />
          {!focused && <FauxCaret aria-hidden="true" />}
        </FieldFrame>
      </SceneTop>
      <SceneSpacer />
      <SceneBottom>
        <PrimaryCta type="button" onClick={submit} disabled={!hasText}>
          CONTINUE
        </PrimaryCta>
        <Ghost
          type="button"
          onClick={() => {
            trackEmerald('emerald_name_skipped');
            dispatch({ type: 'SKIP_NAME' });
          }}
        >
          skip
        </Ghost>
      </SceneBottom>
    </Scene>
  );
};
